import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import "./App.css";
import { Loader, Notification } from "./components/Alerts";
import {useLoaderStore, useUserStore} from "./context";
import storage from "./utils/storage";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import cookieJar from "./utils/cookieJar";
import ViewController from "./components/Layouts/ViewController";
import {useTenant} from './hooks/index';
import {isNil} from "lodash/lang";
import useSWR from "swr";
import {useApi, useSubdomain} from "./hooks";
import {useActiveTenant, useSystemTenantLists, useTenantActions, useTenantTheme} from "./context/useSystemTenantStore";
import {useCookies} from "react-cookie";
import { changeFavicon, isNonProdHost, tryGetSubdomain, userHasRole } from "./utils/helpers";
import constants from "./utils/constants";
import { useNavigate } from "react-router-dom";
import useDevManager from "./context/useDevManager";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
// Stripe publishable key. Use the TEST key on non-prod hosts (Azure dev slot,
// localhost, dev domain — isNonProdHost covers *.azurewebsites.net), and the LIVE
// key only on prod domains. The dev slot runs against QA Margot, so it must use
// Stripe test mode. NOTE: a per-tenant theme stripePublicKey still overrides this
// fallback at load time (see stripePromise below).
const STRIPE_PUBLIC_KEY = isNonProdHost()
  ? "pk_test_51OcCpVKS7DupIgmojiZnZYbKh719wfuRUbK6EEW9fVZTyDrjpyJg8PiYV5HuGX6RaOiVMcSXFpkb6NUeIf5Hlzzk001wFzksAr"
  : "pk_live_51OcCpVKS7DupIgmokb3hc5ntWy6GQsc9Rxa7mafxTeh85gDpGb307Ld61ybZhUAxmh3DNxh6WktDTHupU4Eje8tQ00CdisD0r0";

const queryClient = new QueryClient();

function App() {
  const navigate = useNavigate();
  const {fetch} = useApi();
  const { currentUser, setCurrentUser, setImpersonating, impersonating } = useUserStore();
  const {setShowLoader} = useLoaderStore();
  const { isLoading, loadTenantList, subdomain } = useTenant();
  const activeTenant = useActiveTenant();
  const tenantList = useSystemTenantLists();
  const { setSystemTenants, setActiveSystemTenant } = useTenantActions();
  const [cookies, setCookie, removeCookie] = useCookies([
    cookieJar.ITEMS.token, 
    cookieJar.ITEMS.systemTenants, 
    cookieJar.ITEMS.currentUser, 
    cookieJar.ITEMS.impersonating,
    cookieJar.ITEMS.activeSystemTenantId,
    cookieJar.ITEMS.theme
  ]);
  const theme = useTenantTheme();
  const currentSubdomain = tryGetSubdomain();
  const isSystemTenantAdmin = !currentSubdomain && currentUser &&  userHasRole(currentUser, [constants.ROLE_IDS.ADMIN, constants.ROLE_IDS.SYS_ADMIN]);
  const termsAndConditionsAccepted = isSystemTenantAdmin && currentUser.termsAndConditionsAccepted;
  const privacyPolicyAccepted = isSystemTenantAdmin && currentUser.privacyPolicyAccepted;
  const lastLocation = useRef(window.location.href);
  const { IS_DEV } = useDevManager();
  function resolveFaviconPath() {
    if (!!theme?.logo && !!subdomain)
      return theme?.logo;
    if (IS_DEV)
      return "public/favicon.ico";
    return `${window.location.origin}/favicon.ico`;
  }

  useEffect(() => {
    // const activeSystemTenantIdCookie = cookieJar.getItem(cookieJar.ITEMS.activeSystemTenantId);
    // const systemTenantsCookie = cookieJar.getItem(cookieJar.ITEMS.systemTenants);
    // const user = cookieJar.getItem(cookieJar.ITEMS.currentUser);
    // const isImpersonating = cookieJar.getItem(cookieJar.ITEMS.impersonating) || false;
    const activeSystemTenantCookie = cookies[cookieJar.ITEMS.activeSystemTenant];
    const systemTenantsCookie = cookies[cookieJar.ITEMS.systemTenants];
    const user = cookies[cookieJar.ITEMS.currentUser];
    const isImpersonating = cookies[cookieJar.ITEMS.impersonating] || false;
    setSystemTenants(systemTenantsCookie);
    setCurrentUser(user);
    setImpersonating(isImpersonating);
    if (activeSystemTenantCookie)
      setActiveSystemTenant(activeSystemTenantCookie);
  // }, [setCurrentUser, setImpersonating]);
  }, []);

  useEffect(() => {
    if(!isNil(cookies[cookieJar.ITEMS.token]) && tenantList.length === 0) {
        loadTenantList();
    }
  }, [cookies]);

  useEffect(() => {
      setShowLoader(isLoading);
  }, [isLoading]);

  useEffect(() => {
    let faviconSource = resolveFaviconPath();
    changeFavicon(faviconSource);
  }, [theme?.logo, subdomain]);
  
  useEffect(() => {
    if (currentSubdomain && (!activeTenant || !activeTenant?.id)) {
      fetch(`public/ResolveSystemTenant`)
      .then(res => {
        if (res) setActiveSystemTenant(res);
      })
      .catch ((error) =>{
        console.error(error);
      });
    }
  }, [currentSubdomain]);

  useEffect(() => {
    if (!currentUser || userHasRole(currentUser, constants.ROLE_IDS.CUSTOMER) || lastLocation.current === window.location.href) return;
    lastLocation.current = window.location.href;
    if (!termsAndConditionsAccepted)
      navigate("/termsAndConditions");
    if (!privacyPolicyAccepted)
      navigate("/privacyPolicy");
  }, [currentUser, window.location.href]);

  // useEffect(() => {
  //   console.log("Stripe Public Key changed -----> ", cookies[cookieJar.ITEMS.theme]?.stripePublicKey);
  // }, [cookies[cookieJar.ITEMS.theme]]);

  // useEffect(() => {
  //   console.log("theme", theme);
  //   // let themeCookie = cookies[cookieJar.ITEMS.theme];
  //   // if (!!themeCookie && themeCookie.id !== theme.id) {

  //   // }
  // }, [cookies[cookieJar.ITEMS.theme]]);

  const stripePromise = useCallback(loadStripe(
    cookies[cookieJar.ITEMS.theme]?.stripePublicKey || STRIPE_PUBLIC_KEY
  ), [cookies[cookieJar.ITEMS.theme]]);

  return (
    <div className="h-full mx-auto bg-backgroundColor overflow-x-hidden overflow-y-auto">
      <QueryClientProvider client={queryClient}>
        <Notification />
        <Loader />
        <Elements stripe={stripePromise}>
          <ViewController/>
        </Elements>
      </QueryClientProvider>
    </div>
  );
}

export default App;
